import { __, sprintf } from "@wordpress/i18n";
import { useMemo, useState } from "@wordpress/element";
import { Spinner } from "@wordpress/components";
import { Table } from "../../components/table";
import { AJAX_ACTION, INPUT, NONCE, OPTIONS, POST_TYPES } from "../../utils/constants";
import { ajax } from "../../config/axios";
import { Term } from "../../types/post";
import { Clamp } from "../../components/clamp";
import { toast } from "react-toastify";
import { SwitchBox } from "@/components/switch";
import { css } from "@/utils/css";
import { usePosts } from "@/hooks/use-posts";
import { NumberInput } from "@/components/number-input";
import { Select } from "@/components/select";
import { useModal } from "@/hooks/use-modal";
import { parse } from "@wordpress/block-serialization-default-parser";
import { TextareaInput } from "@/components/textarea-input";
import { api } from "@/config/api-fetch";
import { Section } from "@/components/section";
import { Button } from "@/components/button";
import { suggestions } from "./suggestions";
import { useUsageStats } from "@/hooks/use-usage-stats";
import { IS_PRO } from "@/utils/constants";
import { SimpleProAlert, useProModal } from "@/components/pro-alert-box";

const postTypes = POST_TYPES.filter((postType) => postType.supportsExcerpt || postType.taxonomies.length > 0);
const ACTIONS = {
    REWRITE_CONTENT: "rewrite-content",
    GENERATE_EXCERPTS: "generate-excerpts",
    TAXONOMY_PREFIX: "generate-taxonomy",
};
const taxonomyActionValue = (taxonomyId: string) => `${ACTIONS.TAXONOMY_PREFIX}-${taxonomyId}`;

export default function BulkRewrite() {
    const [selectedPostType, setSelectedPostType] = useState(postTypes[0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [postsOnGenerateExcerpts, setPostsOnGenerateExcerpts] = useState<string[]>([]);
    const [postsOnGenerateTerms, setPostsOnGenerateTerms] = useState<Record<string, string[]>>({});
    const [postsOnRewrite, setPostsOnRewrite] = useState<string[]>([]);
    const [excerptLength, setExcerptLength] = useState(INPUT.EXCERPT_LENGTH.DEFAULT);
    const [overwrite, setOverwrite] = useState(false);
    const [numberOfTerms, setNumberOfTerms] = useState<Record<string, number>>({});
    const [termsThreshold, setTermsThreshold] = useState<Record<string, number>>({});
    const [rewritePrompt, setRewritePrompt] = useState("");

    const { data, refetch, isLoading } = usePosts(selectedPostType.id, currentPage);
    const { openModal, closeModal } = useModal();
    const { showProAlert } = useProModal();
    
    const { data: usageStats, refetch: refetchUsage } = useUsageStats();

    const rows = useMemo(
        () =>
            data?.posts?.map((post) => {
                const post_content_html = parse(post.post_content)
                    .map((block) => block.innerHTML)
                    .join("\n");
                const text_content = new DOMParser().parseFromString(post_content_html, "text/html").body.textContent || "";

                return {
                    id: `${post.ID}`,
                    post_title: (
                        <a href={post.permalink} target="_blank">
                            {post.post_title || __("(no title)", "antimanual")}
                        </a>
                    ),
                    actions: [
                        {
                            label: __("Edit", "antimanual"),
                            url: post.edit_link,
                            target: "_blank",
                        },
                        {
                            label: __("View", "antimanual"),
                            url: post.permalink,
                            target: "_blank",
                        },
                    ],
                    post_content: postsOnRewrite.includes(`${post.ID}`) ? (
                        <Spinner />
                    ) : (
                        <>
                            {text_content.length > 100 ? (
                                <>
                                    {text_content.substring(0, 100) + "... "}
                                    <a
                                        className={css("cursor-pointer")}
                                        href={post.permalink}
                                        target="_blank"
                                    >
                                        {__("Read more", "antimanual")}
                                    </a>
                                </>
                            ) : (
                                text_content
                            )}
                        </>
                    ),
                    post_excerpt: postsOnGenerateExcerpts.includes(`${post.ID}`) ? <Spinner /> : <Clamp>{post.post_excerpt || "—"}</Clamp>,
                    ...selectedPostType.taxonomies.reduce(
                        (acc, taxonomy) => {
                            const terms = post[taxonomy.id] as Term[];
                            const isLoading = !!postsOnGenerateTerms[taxonomy.id]?.includes(`${post.ID}`);

                            acc[taxonomy.id] = isLoading ? <Spinner /> : <Clamp>{terms?.map((term) => term.name).join(", ") || "—"}</Clamp>;
                            return acc;
                        },
                        {} as Record<string, React.ReactNode>
                    ),
                };
            }),
        [data, postsOnGenerateExcerpts, postsOnGenerateTerms, postsOnRewrite, selectedPostType]
    );

    const columns = [
        {
            id: "post_title",
            label: __("Title", "antimanual"),
            sortable: false,
        },
        {
            id: "post_content",
            label: __("Content", "antimanual"),
            sortable: false,
        },
        ...(selectedPostType.supportsExcerpt
            ? [
                {
                    id: "post_excerpt",
                    label: __("Excerpt", "antimanual"),
                    sortable: false,
                },
            ]
            : []),

        ...selectedPostType.taxonomies,
    ];

    const actions = [
        {
            value: ACTIONS.REWRITE_CONTENT,
            label: __("Rewrite Content", "antimanual"),
            handler: async (selectedItems: string[]) => {
                const prompt = rewritePrompt.trim();

                if (!prompt) {
                    toast.error(__("Please provide a rewrite prompt.", "antimanual"));
                    return;
                }

                if (!selectedItems.length) {
                    toast.error(__("Please select at least one post.", "antimanual"));
                    return;
                }

                if (usageStats?.bulk_rewrite && usageStats.bulk_rewrite.count >= usageStats.bulk_rewrite.limit && !IS_PRO) {
                    showProAlert(
                        __("Monthly Limit Exceeded", "antimanual"),
                        __("You have reached your monthly limit of bulk rewrite actions. Upgrade to Pro for unlimited actions.", "antimanual")
                    );
                    return;
                }

                await Promise.all(
                    selectedItems.map(async (postId) => {
                        setPostsOnRewrite((prev) => [...prev, postId]);

                        try {
                            await api.post(`/rewrite-content`, { postId, prompt });
                            await refetch();
                            await refetchUsage();

                            toast.success(__("Content rewrite completed.", "antimanual"));
                        } catch (e: any) {
                            if (e?.code === 'monthly_limit_exceeded') {
                                showProAlert(
                                    __("Monthly Limit Exceeded", "antimanual"),
                                    e?.message
                                );
                            } else {
                                toast.error(e?.message || __("Content rewrite failed.", "antimanual"));
                            }
                        }

                        setPostsOnRewrite((prev) => prev.filter((id) => id !== postId));
                    })
                );
            },
        },
        ...(selectedPostType.supportsExcerpt
            ? [
                {
                    value: ACTIONS.GENERATE_EXCERPTS,
                    label: __("Generate Excerpts", "antimanual"),
                    handler: async (selectedItems: string[]) => {
                        const processPost = async (postId: string) => {
                            setPostsOnGenerateExcerpts((prev) => [...prev, postId]);

                            try {
                                const { excerpt } = await ajax<{ excerpt: string }>({
                                    action: AJAX_ACTION.GET_EXCERPT,
                                    nonce: NONCE.GET_EXCERPT,
                                    post_id: postId,
                                    overwrite: Number(overwrite),
                                    excerpt_length: excerptLength,
                                });

                                await ajax<{ excerpt: string }>({
                                    action: AJAX_ACTION.SET_TERMS,
                                    nonce: NONCE.SET_TERMS,
                                    post_id: postId,
                                    excerpt: excerpt,
                                });

                                await refetch();
                                await refetchUsage();
                            } catch (error: any) {
                                if (error?.code === 'monthly_limit_exceeded') {
                                    showProAlert(
                                        __("Monthly Limit Exceeded", "antimanual"),
                                        error?.message
                                    );
                                } else {
                                    toast.error(error?.message || __("Excerpt generation failed", "antimanual"));
                                }
                            } finally {
                                setPostsOnGenerateExcerpts((prev) => prev.filter((id) => id !== postId));
                            }
                        };

                        if (usageStats?.bulk_rewrite && usageStats.bulk_rewrite.count >= usageStats.bulk_rewrite.limit && !IS_PRO) {
                             showProAlert(
                                __("Monthly Limit Exceeded", "antimanual"),
                                __("You have reached your monthly limit of bulk rewrite actions. Upgrade to Pro for unlimited actions.", "antimanual")
                            );
                            return;
                        }

                        await Promise.all(selectedItems.map((postId) => processPost(postId)));
                    },
                },
            ]
            : []),
        ...selectedPostType.taxonomies.map((taxonomy) => ({
            value: taxonomyActionValue(taxonomy.id),
            label: __("Generate", "antimanual") + ` ${taxonomy.label}`,
            handler: async (selectedItems: string[]) => {
                const processPost = async (postId: string) => {
                    setPostsOnGenerateTerms((prev) => ({ ...prev, [taxonomy.id]: [...(prev[taxonomy.id] || []), postId] }));

                    try {
                        const { taxonomy_terms: terms } = await ajax<{ taxonomy_terms: number[]; content: string }>({
                            action: AJAX_ACTION.GET_TAXONOMY_TERMS,
                            nonce: NONCE.GET_TAXONOMY_TERMS,
                            post_id: postId,
                            taxonomy: taxonomy.id,
                            number_of_terms: numberOfTerms[taxonomy.id] || INPUT.NUMBER_OF_TERMS.DEFAULT,
                            terms_threshold: termsThreshold[taxonomy.id] || INPUT.TERMS_THRESHOLD.DEFAULT,
                        });

                        await ajax({
                            action: AJAX_ACTION.SET_TAXONOMY_TERMS,
                            nonce: NONCE.SET_TAXONOMY_TERMS,
                            post_id: postId,
                            taxonomy: taxonomy.id,
                            terms: terms,
                        });

                        await refetch();
                        await refetchUsage();
                    } catch (error: any) {
                        if (error?.code === 'monthly_limit_exceeded') {
                            showProAlert(
                                __("Monthly Limit Exceeded", "antimanual"),
                                error?.message
                            );
                        } else {
                            toast.error(error?.message || __("Taxonomy terms generation failed", "antimanual"));
                        }
                    } finally {
                        setPostsOnGenerateTerms((prev) => ({ ...prev, [taxonomy.id]: prev[taxonomy.id].filter((id) => id !== postId) }));
                    }
                };

                if (usageStats?.bulk_rewrite && usageStats.bulk_rewrite.count >= usageStats.bulk_rewrite.limit && !IS_PRO) {
                     showProAlert(
                        __("Monthly Limit Exceeded", "antimanual"),
                        __("You have reached your monthly limit of bulk rewrite actions. Upgrade to Pro for unlimited actions.", "antimanual")
                    );
                    return;
                }

                await Promise.all(selectedItems.map((postId) => processPost(postId)));
            },
        })),
    ];

    const [selectedActions, setSelectedActions] = useState([actions[0]?.value]);

    const handleApplyActions = async (selectedItems: string[], actionValues: string[]) => {
        const actionHandlers = actions.filter((action) => actionValues.includes(action.value)).map((action) => action.handler);

        await Promise.all(
            selectedItems.map(async (postId) => {
                for (const handler of actionHandlers) {
                    await handler([postId]);
                }
            })
        );
    };

    return (
        <div className={css("pt-6 pb-12")}>
            <div className={css("grid grid-cols-2 gap-6 pt-3 pb-6")}>
                <Section className={css("grid grid-cols-2 gap-4")}>
                    <Select
                        label={__("Select Post Type", "antimanual")}
                        value={selectedPostType.id}
                        options={postTypes.map((pt) => ({ value: pt.id, label: pt.label }))}
                        onChange={(id) => {
                            const postType = postTypes.find((pt) => pt.id === id);
                            if (postType) {
                                setSelectedPostType(postType);
                                setSelectedActions([ACTIONS.REWRITE_CONTENT]);
                            }
                        }}
                    />
                    <Select
                        isMulti
                        value={selectedActions}
                        label={__("Select Actions", "antimanual")}
                        options={actions}
                        onChange={(actions) => {
                            if (actions.length) {
                                setSelectedActions(actions);
                            }
                        }}
                    />
					{!IS_PRO && usageStats?.bulk_rewrite && (
						<div className={css("col-span-2")}>
							<SimpleProAlert>
								{sprintf(
                                    __("Usage: %1$d / %2$d actions this month", "antimanual"),
                                    usageStats.bulk_rewrite.count,
                                    usageStats.bulk_rewrite.limit
                                )}
							</SimpleProAlert>
						</div>
					)}
                </Section>

                {selectedActions.includes(ACTIONS.REWRITE_CONTENT) && (
                    <Section>
                        <TextareaInput
                            rows={5}
                            label={
                                <div className={css("flex items-center justify-between")}>
                                    <span>{__("Rewrite Prompt", "antimanual")}</span>
                                    <Button
                                        small
                                        onClick={() => {
                                            openModal(
                                                <div className={css("p-12 max-w-4xl")}>
                                                    <h2 className={css("text-2xl font-bold mb-4 text-center pb-8")}>
                                                        {__("Rewrite Prompt Suggestions", "antimanual")}
                                                    </h2>
                                                    <ul className={css("grid grid-cols-2 gap-6")}>
                                                        {suggestions.map((suggestion) => (
                                                            <li
                                                                key={suggestion.title}
                                                                onClick={() => {
                                                                    setRewritePrompt(suggestion.prompt);
                                                                    closeModal();
                                                                }}
                                                                className={css("p-6 rounded-4 bs-sm hover")}
                                                            >
                                                                <h3 className={css("font-semibold")}>{suggestion.title}</h3>
                                                                <p className={css("whitespace-pre-wrap")}>{suggestion.prompt}</p>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            );
                                        }}
                                    >
                                        {__("💡Suggestions", "antimanual")}
                                    </Button>
                                </div>
                            }
                            help={__(
                                "Enter a detailed prompt to guide the AI in rewriting the selected posts' content. For example, specify tone, style, or specific changes like improving readability or adding keywords.",
                                "antimanual"
                            )}
                            placeholder={__(
                                "Rewrite in a professional, engaging tone for business audiences. Simplify complex sentences for better readability, fix grammatical errors, add SEO keywords (e.g., 'digital marketing strategies'), ensure logical structure with clear headings, maintain similar word count, and enhance persuasiveness.",
                                "antimanual"
                            )}
                            value={rewritePrompt}
                            onChange={setRewritePrompt}
                        />
                    </Section>
                )}

                {selectedActions.includes(ACTIONS.GENERATE_EXCERPTS) && (
                    <Section className={css("grid grid-cols-2 gap-4")}>
                        <h2 className={css("m-0 mt-3 mb-2 col-span-2")}>{__("Generate Excerpts", "antimanual")}</h2>
                        <SwitchBox
                            label={__("Overwrite Existing Excerpt", "antimanual")}
                            help={__("Enable to overwrite the excerpt that already exists.", "antimanual")}
                            checked={overwrite}
                            onChange={setOverwrite}
                        />
                        <NumberInput
                            label={__("Excerpt Length (Words) s", "antimanual")}
                            help={__("How many words should the excerpt contain?", "antimanual")}
                            min={INPUT.EXCERPT_LENGTH.MIN}
                            max={INPUT.EXCERPT_LENGTH.MAX}
                            value={excerptLength}
                            onChange={setExcerptLength}
                        />
                    </Section>
                )}

                {
                    selectedPostType.taxonomies.map(taxonomy => {
                        const action = actions.find(action => action.value === taxonomyActionValue(taxonomy.id));

                        if (!action || !selectedActions.includes(action.value)) return null;

                        return <Section key={taxonomy.id} className={css("grid grid-cols-2 gap-4")}>
                            <h2 className={css("m-0 mt-3 mb-2 col-span-2")}>{action.label}</h2>
                            <NumberInput
                                label={`Number of ${taxonomy.label}`}
                                help={`How many ${taxonomy.label} to generate for each post?`}
                                min={INPUT.NUMBER_OF_TERMS.MIN}
                                max={INPUT.NUMBER_OF_TERMS.MAX}
                                value={numberOfTerms[taxonomy.id] || INPUT.NUMBER_OF_TERMS.DEFAULT}
                                onChange={value => setNumberOfTerms({ ...numberOfTerms, [taxonomy.id]: value })}
                            />
                            <NumberInput
                                label={`Terms Threshold for ${taxonomy.label}`}
                                help={`Maximum number of existing ${taxonomy.label} a post can have to be eligible for term generation.`}
                                min={INPUT.TERMS_THRESHOLD.MIN}
                                max={INPUT.TERMS_THRESHOLD.MAX}
                                value={termsThreshold[taxonomy.id] || INPUT.TERMS_THRESHOLD.DEFAULT}
                                onChange={value => setTermsThreshold({ ...termsThreshold, [taxonomy.id]: value })}
                            />
                        </Section>
                    })
                }
            </div>
            <Table
                key={selectedPostType.id + currentPage}
                columns={columns}
                rows={rows || []}
                actions={[
                    {
                        label:
                            selectedActions
                                .map((action) => actions.find((a) => a.value === action)?.label)
                                .filter(Boolean)
                                .join(", ") || __("No Action Selected", "antimanual"),
                        value: "apply",
                        handler: (selectedItems) => handleApplyActions(selectedItems, selectedActions),
                    },
                ]}
                pagination={{
                    total: data?.total || 0,
                    currentPage,
                    limit: OPTIONS.PAGINATION_LIMIT,
                    onPageChange: setCurrentPage,
                }}
                rowsLoading={isLoading}
                selectedAction={"apply"}
            />
        </div >
    );
}
